import React, { useCallback, useEffect, useState } from 'react';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { formatApiErrorDetail } from '../../utils/apiErrorDetail';
import {
  getSequenceCheck,
  ignoreRecommendation,
  runSequenceCheck,
  showRecommendationAgain,
} from '../../api/sequenceCheck';
import {
  SEQUENCE_CHANGED_HINT,
  SEQUENCE_CHECK,
  SEQUENCE_CHECK_ADVICE_NOTE,
  SEQUENCE_CHECK_ALL_DECIDED,
  SEQUENCE_CHECK_BUSY,
  SEQUENCE_CHECK_FAILED,
  SEQUENCE_CHECK_SUMMARY,
  SEQUENCE_CHECK_WHEN,
  SEQUENCE_DECIDED_TOGGLE,
  SEQUENCE_IGNORE,
  SEQUENCE_IGNORED_TOGGLE,
  SEQUENCE_RELATED,
  SEQUENCE_SHOW_AGAIN,
  SEQUENCE_SHOWN_NEXT_CHECK,
  SEQUENCE_STATUS_CHANGED,
  SEQUENCE_STATUS_NEEDS,
  SEQUENCE_STATUS_NO_TALK,
  SEQUENCE_STATUS_WARN,
} from '../../utils/writerCopy';

/** Where a sequence stands in the last check (words, not colour). */
export function sequenceStatus(seq) {
  const codes = (seq?.reasons || []).map((r) => r.code);
  if (seq?.changed_since_check) return 'changed';
  if (seq?.dismissed) return 'ignored';
  if (codes.includes('no_talk')) return 'no_talk';
  if (!seq?.ready) return 'needs';
  if (codes.includes('off_purpose') && seq?.recommendation) return 'warn';
  return 'decided';
}

const STATUS_LABEL = {
  changed: SEQUENCE_STATUS_CHANGED,
  no_talk: SEQUENCE_STATUS_NO_TALK,
  needs: SEQUENCE_STATUS_NEEDS,
  warn: SEQUENCE_STATUS_WARN,
};

function whenLabel(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function errorCopy(err) {
  const raw = formatApiErrorDetail(err?.detail) || formatApiErrorDetail(err?.message) || '';
  const cleaned = raw.replace(/^VALIDATION:\s*[^-]+-\s*/, '');
  return cleaned && !/internal error|\[object Object\]/i.test(cleaned) ? cleaned : SEQUENCE_CHECK_FAILED;
}

export function Recommendation({ rec, statusLabel }) {
  if (!rec) return null;
  // The plain "Not discussed yet" headline repeats the status; say it once.
  const headline = rec.headline && rec.headline !== statusLabel ? rec.headline : null;
  return (
    <div className="pt-seqcheck__rec">
      {headline && <p className="pt-seqcheck__headline">{headline}</p>}
      {rec.what_we_noticed && <p className="pt-seqcheck__text">{rec.what_we_noticed}</p>}
      {(rec.quotes || []).map((quote) => (
        <blockquote className="pt-seqcheck__quote" key={quote}>{quote}</blockquote>
      ))}
      {rec.suggestion && <p className="pt-seqcheck__text">{rec.suggestion}</p>}
      {(rec.questions || []).length > 0 && (
        <ul className="pt-seqcheck__questions">
          {rec.questions.map((q) => <li key={q}>{q}</li>)}
        </ul>
      )}
      {rec.related_sequence && (
        <p className="pt-check__meta">{SEQUENCE_RELATED}: {rec.related_sequence}</p>
      )}
    </div>
  );
}

/**
 * Check my sequences. Peer to Check outline: runs only when the writer clicks
 * (it spends on their key, after the cost dialog). The last check is shown on
 * open (free) and stays until the next one. Advice only; any of it can be ignored.
 */
export default function PlotTrackSequenceCheck({ storyId, runSignal = 0 }) {
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const [showDecided, setShowDecided] = useState(false);
  const [notes, setNotes] = useState({});

  const refresh = useCallback(async () => {
    if (!storyId) return;
    try {
      const res = await getSequenceCheck(storyId);
      setCheck(res?.check || null);
    } catch {
      setCheck(null);
    }
  }, [storyId]);

  useEffect(() => {
    setCheck(null);
    setError(null);
    setNotes({});
    setShowIgnored(false);
    setShowDecided(false);
    refresh();
  }, [refresh]);

  const onRun = async () => {
    if (!storyId || busy) return;
    const ok = await askPaid('sequence_check');
    if (!ok) return;
    setBusy(true);
    setError(null);
    setNotes({});
    try {
      setCheck(await runSequenceCheck(storyId));
    } catch (err) {
      setError(errorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  // The pre-Generate panel's "Check my sequences" starts a check here.
  useEffect(() => {
    if (runSignal) onRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runSignal]);

  const onIgnore = async (seq) => {
    try {
      await ignoreRecommendation(storyId, seq.name, seq.talk_fingerprint);
      await refresh();
    } catch (err) {
      setError(errorCopy(err));
    }
  };

  const onShowAgain = async (seq) => {
    try {
      await showRecommendationAgain(storyId, seq.name);
      const res = await getSequenceCheck(storyId);
      const next = res?.check || null;
      setCheck(next);
      const back = (next?.sequences || []).find((s) => s.name === seq.name);
      if (back && !back.recommendation) setNotes((prev) => ({ ...prev, [seq.name]: SEQUENCE_SHOWN_NEXT_CHECK }));
    } catch (err) {
      setError(errorCopy(err));
    }
  };

  const sequences = check?.sequences || [];
  const byStatus = (wanted) => sequences.filter((s) => wanted.includes(sequenceStatus(s)));
  const attention = byStatus(['changed', 'needs', 'no_talk', 'warn']);
  const decided = byStatus(['decided']);
  const ignored = byStatus(['ignored']);
  const canIgnore = check?.dismiss_available !== false;

  return (
    <>
      {paidDialog}
      <button
        type="button"
        className="pt-generate__btn pt-check__btn"
        onClick={onRun}
        disabled={busy || !storyId}
        aria-label={busy ? SEQUENCE_CHECK_BUSY : SEQUENCE_CHECK}
      >
        {busy ? SEQUENCE_CHECK_BUSY : SEQUENCE_CHECK}
      </button>
      {(busy || error || check) && (
        <div className="pt-check pt-seqcheck" role="region" aria-label="Sequence check" aria-live="polite">
          {busy && <p className="pt-check__status">{SEQUENCE_CHECK_BUSY}</p>}
          {!busy && error && <p className="pt-check__status" role="alert">{error}</p>}
          {!busy && check && (
            <>
              <p className="pt-check__status">
                {SEQUENCE_CHECK_SUMMARY(check.ready_count ?? 0, check.total ?? sequences.length)}
                {' '}
                {SEQUENCE_CHECK_WHEN(whenLabel(check.checked_at))}
              </p>
              {attention.length === 0 && (
                <p className="pt-check__status" role="status">{SEQUENCE_CHECK_ALL_DECIDED}</p>
              )}
              {attention.length > 0 && (
                <>
                  <p className="pt-check__meta">{SEQUENCE_CHECK_ADVICE_NOTE}</p>
                  <ul className="pt-check__list" aria-label="Sequences to look at">
                    {attention.map((seq) => {
                      const status = sequenceStatus(seq);
                      return (
                        <li className="pt-check__item" key={seq.name} data-status={status}>
                          <p className="pt-check__severity">{STATUS_LABEL[status]}</p>
                          <p className="pt-check__finding">{seq.name}</p>
                          {status === 'changed'
                            ? <p className="pt-check__meta">{SEQUENCE_CHANGED_HINT}</p>
                            : <Recommendation rec={seq.recommendation} statusLabel={STATUS_LABEL[status]} />}
                          {status !== 'changed' && seq.recommendation && (
                            <button
                              type="button"
                              className="pt-generate__btn pt-check__btn pt-seqcheck__ignore"
                              onClick={() => onIgnore(seq)}
                              disabled={!canIgnore}
                            >
                              {SEQUENCE_IGNORE}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {decided.length > 0 && (
                <details className="pt-seqcheck__group" open={showDecided} onToggle={(e) => setShowDecided(e.currentTarget.open)}>
                  <summary>{SEQUENCE_DECIDED_TOGGLE(decided.length)}</summary>
                  <ul className="pt-seqcheck__names">
                    {decided.map((seq) => <li key={seq.name}>{seq.name}</li>)}
                  </ul>
                </details>
              )}
              {ignored.length > 0 && (
                <details className="pt-seqcheck__group" open={showIgnored} onToggle={(e) => setShowIgnored(e.currentTarget.open)}>
                  <summary>{SEQUENCE_IGNORED_TOGGLE(ignored.length)}</summary>
                  <ul className="pt-seqcheck__names">
                    {ignored.map((seq) => (
                      <li key={seq.name}>
                        <span>{seq.name}</span>
                        {notes[seq.name]
                          ? <span className="pt-check__meta"> {notes[seq.name]}</span>
                          : (
                            <button type="button" className="pt-generate__btn pt-check__btn" onClick={() => onShowAgain(seq)}>
                              {SEQUENCE_SHOW_AGAIN}
                            </button>
                          )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
